import React, { useState } from 'react';
import { useQuery } from 'react-query';
import { Link } from 'react-router-dom';
import { memberService } from '../../services/memberService';
import { PlusIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline';
import { formatDate, formatCurrency, getStatusColor, formatPhoneNumber } from '../../utils/formatters';
import LoadingSpinner from '../Common/LoadingSpinner';
import ErrorMessage from '../Common/ErrorMessage';


const MemberList = () => {
  const [page, setPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [searchResults, setSearchResults] = useState(null);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState(null);

  const { data, isLoading, error } = useQuery(
    ['members', page, statusFilter],
    () => memberService.getMembers({ page, limit: 20, ...(statusFilter && { status: statusFilter }) }),
    { keepPreviousData: true }
  );

  // Debounced live search
  React.useEffect(() => {
    const handler = setTimeout(() => {
      const doSearch = async () => {
        if (searchTerm.length >= 2) {
          setSearchLoading(true);
          setSearchError(null);
          try {
            const res = await memberService.searchMembers(searchTerm);
            if (res.success) {
              setSearchResults(res.data || []);
            } else {
              setSearchResults([]);
            }
          } catch (err) {
            setSearchError('Failed to search members');
            setSearchResults([]);
          } finally {
            setSearchLoading(false);
          }
        } else {
          setSearchResults(null);
        }
      };
      doSearch();
    }, 400); // 400ms debounce
    return () => clearTimeout(handler);
  }, [searchTerm]);

  const handleClearSearch = () => {
    setSearchTerm('');
    setSearchResults(null);
    setSearchError(null);
  };

  if (isLoading) return <LoadingSpinner />;
  if (error) return <ErrorMessage message="Failed to load members" />;
  if (searchLoading) return <LoadingSpinner />;
  if (searchError) return <ErrorMessage message={searchError} />;

  const members = searchResults !== null ? searchResults : (data?.data || []);
  const pagination = data?.pagination || {};

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-2xl font-semibold text-gray-900">Members</h1>
        <Link to="/members/new" className="btn-primary flex items-center">
          <PlusIcon className="h-5 w-5 mr-2" />
          Add New Member
        </Link>
      </div>

      <div className="card">
        <div className="flex gap-4 mb-6">
          <div className="flex-1 relative">
            <input
              type="text"
              placeholder="Search by name, phone, NID, or member code..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="input-field pl-10"
            />
            <MagnifyingGlassIcon className="h-5 w-5 text-gray-400 absolute left-3 top-3" />
            {searchResults !== null && (
              <button
                onClick={handleClearSearch}
                className="absolute right-3 top-2 text-gray-400 hover:text-gray-600"
                title="Clear search"
              >
                ✕
              </button>
            )}
          </div>

          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="input-field w-48"
            disabled={searchResults !== null}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="closed">Closed</option>
          </select>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="table-header">Member Code</th>
                <th className="table-header">Name</th>
                <th className="table-header">Phone</th>
                <th className="table-header">Joining Date</th>
                <th className="table-header">Status</th>
                <th className="table-header">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {members.length === 0 ? (
                <tr>
                  <td colSpan="6" className="text-center py-4 text-gray-500">No members found.</td>
                </tr>
              ) : (
                members.map((member) => (
                  <tr key={member.id} className="hover:bg-gray-50">
                    <td className="table-cell font-medium">{member.member_code}</td>
                    <td className="table-cell">{member.name}</td>
                    <td className="table-cell">{formatPhoneNumber(member.phone)}</td>
                    <td className="table-cell">{formatDate(member.joining_date)}</td>
                    <td className="table-cell">
                      <span className={`px-2 py-1 text-xs rounded-full ${getStatusColor(member.status)}`}>
                        {member.status}
                      </span>
                    </td>
                    <td className="table-cell">
                      <Link
                        to={`/members/${member.id}`}
                        className="text-primary-600 hover:text-primary-900 mr-3"
                      >
                        View
                      </Link>
                      <Link
                        to={`/members/${member.id}/edit`}
                        className="text-blue-600 hover:text-blue-900"
                      >
                        Edit
                      </Link>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Show pagination only if not searching */}
        {searchResults === null && pagination.pages > 1 && (
          <div className="flex justify-between items-center mt-6">
            <div className="text-sm text-gray-700">
              Showing {((pagination.page - 1) * pagination.limit) + 1} to{' '}
              {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} results
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => setPage(p => Math.max(1, p - 1))}
                disabled={pagination.page === 1}
                className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Previous
              </button>
              <button
                onClick={() => setPage(p => Math.min(pagination.pages, p + 1))}
                disabled={pagination.page === pagination.pages}
                className="px-3 py-1 border rounded disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default MemberList;